import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import type { AtsJobDetails } from "../../types";
import { atsPdfStyles } from "../atsPdfStyles";

export interface DefaultAtsPdfDocumentProps {
  jobDetails: AtsJobDetails;
  /** Increment when adding new visual templates */
  templateVersion?: string;
}

const localStyles = StyleSheet.create({
  flex1: { flex: 1 },
});

function PdfHeader({ basic }: { basic: AtsJobDetails["basic_info"] }) {
  const website = basic.linkedin?.trim() || basic.github?.trim() || "";
  const websiteLabel = website
    ? website.replace(/^https?:\/\//, "").replace(/^www\./, "")
    : "";

  return (
    <View>
      <Text style={atsPdfStyles.headerName}>{basic.name}</Text>
      {basic.job_title ? (
        <Text style={atsPdfStyles.headerSubtitle}>{basic.job_title}</Text>
      ) : null}
      {basic.phone ? (
        <Text style={atsPdfStyles.contactRow}>{basic.phone}</Text>
      ) : null}
      {basic.email ? (
        <Text style={atsPdfStyles.contactRow}>{basic.email}</Text>
      ) : null}
      {websiteLabel ? (
        <Text style={atsPdfStyles.contactRow}>{websiteLabel}</Text>
      ) : null}
      {basic.address ? (
        <Text style={atsPdfStyles.contactRow}>{basic.address}</Text>
      ) : null}
      <View style={atsPdfStyles.headerRule} />
    </View>
  );
}

function PdfSectionTitle({ title }: { title: string }) {
  return (
    <View>
      <Text style={atsPdfStyles.sectionTitle}>{title}</Text>
      <View style={atsPdfStyles.sectionRule} />
    </View>
  );
}

/**
 * Default ATS layout for PDF export. Add sibling files (e.g. `MinimalAtsPdfDocument.tsx`)
 * and switch in `ATSResumePdfExport` to offer multiple templates.
 */
export function DefaultAtsPdfDocument({
  jobDetails,
  templateVersion = "default-v1",
}: DefaultAtsPdfDocumentProps) {
  const {
    basic_info: basic,
    experiences,
    educations,
    certificates,
    skills,
    professional_languages,
    others,
  } = jobDetails;

  return (
    <Document
      title={`${basic.name} — Resume`}
      author={basic.name}
      subject="Resume"
      creator={`Knoot ATS / ${templateVersion}`}
    >
      <Page size="A4" style={atsPdfStyles.page}>
        <PdfHeader basic={basic} />

        {experiences && experiences.length > 0 ? (
          <View>
            <PdfSectionTitle title="Experience" />
            {experiences.map((exp, i) => (
              <View
                key={`exp-${exp.company}-${i}`}
                style={{ marginBottom: 10 }}
              >
                <View style={atsPdfStyles.rowBetween}>
                  <View style={localStyles.flex1}>
                    <Text style={atsPdfStyles.expTitle}>{exp.title}</Text>
                    <Text style={atsPdfStyles.expCompany}>{exp.company}</Text>
                  </View>
                  <Text style={atsPdfStyles.expDates}>
                    {exp.start_date} – {exp.end_date}
                  </Text>
                </View>
                {exp.responsibilities?.map((line, j) => (
                  <Text key={j} style={atsPdfStyles.bullet}>
                    • {line}
                  </Text>
                ))}
              </View>
            ))}
          </View>
        ) : null}

        {educations && educations.length > 0 ? (
          <View>
            <PdfSectionTitle title="Education" />
            {educations.map((edu, i) => {
              const year = edu.end_date
                ? new Date(edu.end_date).getFullYear()
                : "";
              return (
                <View
                  key={`edu-${edu.university}-${i}`}
                  style={atsPdfStyles.rowBetween}
                >
                  <View style={localStyles.flex1}>
                    <Text style={atsPdfStyles.expTitle}>{edu.university}</Text>
                    <Text style={atsPdfStyles.muted}>
                      {edu.degree || edu.major}
                    </Text>
                  </View>
                  {year ? (
                    <Text style={atsPdfStyles.expDates}>{String(year)}</Text>
                  ) : null}
                </View>
              );
            })}
          </View>
        ) : null}

        {certificates && certificates.length > 0 ? (
          <View>
            <PdfSectionTitle title="Certificates" />
            {certificates.map((c, i) => {
              const y = c.date ? new Date(c.date).getFullYear() : "";
              return (
                <View
                  key={`cert-${c.name}-${i}`}
                  style={atsPdfStyles.rowBetween}
                >
                  <View style={localStyles.flex1}>
                    <Text style={atsPdfStyles.expTitle}>{c.name}</Text>
                  </View>
                  {y ? (
                    <Text style={atsPdfStyles.expDates}>{String(y)}</Text>
                  ) : null}
                </View>
              );
            })}
          </View>
        ) : null}

        {skills && skills.length > 0 ? (
          <View>
            <PdfSectionTitle title="Skills" />
            {skills.map((s, i) => (
              <View key={`sk-${s.name}-${i}`} style={atsPdfStyles.skillsRow}>
                <View style={atsPdfStyles.skillsCol}>
                  <Text>{s.name}</Text>
                </View>
                <View style={atsPdfStyles.skillsLevel}>
                  <Text style={atsPdfStyles.muted}>{s.level}</Text>
                </View>
              </View>
            ))}
          </View>
        ) : null}

        {professional_languages && professional_languages.length > 0 ? (
          <View>
            <PdfSectionTitle title="Languages" />
            {professional_languages.map((lang, i) => (
              <View
                key={`lang-${lang.name}-${i}`}
                style={atsPdfStyles.rowBetween}
              >
                <Text style={atsPdfStyles.muted}>{lang.name}</Text>
                <Text style={atsPdfStyles.expDates}>{lang.level}</Text>
              </View>
            ))}
          </View>
        ) : null}

        {others?.soft_skills && others.soft_skills.length > 0 ? (
          <View>
            <PdfSectionTitle title="Soft skills" />
            <Text style={atsPdfStyles.muted}>
              {others.soft_skills.join(", ")}
            </Text>
          </View>
        ) : null}

        {others?.awards && others.awards.length > 0 ? (
          <View>
            <PdfSectionTitle title="Awards" />
            {others.awards.map((a, i) => (
              <Text key={i} style={atsPdfStyles.bullet}>
                • {a}
              </Text>
            ))}
          </View>
        ) : null}

        <Text
          style={atsPdfStyles.footer}
          fixed
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} / ${totalPages}`
          }
        />
      </Page>
    </Document>
  );
}
